import React, { useState, useEffect } from 'react';
import { FiCamera, FiUpload, FiGlobe, FiFacebook, FiInstagram, FiTwitter, FiLinkedin, FiEdit3, FiSave, FiX } from 'react-icons/fi';
import { useProfile } from '../../context/ProfileContext';

const FranchiseProfile = () => {
  const { franchiseProfile, updateFranchiseProfile, uploadDocument, saving } = useProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [localProfile, setLocalProfile] = useState(franchiseProfile);
  const [localSaving, setLocalSaving] = useState(false);

  // Sync local profile with global profile when it changes
  useEffect(() => {
    setLocalProfile(franchiseProfile);
  }, [franchiseProfile]);

  const handleInputChange = (field, value, isNested = false, parent = null) => {
    if (isNested && parent) {
      setLocalProfile(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [field]: value
        }
      }));
    } else {
      setLocalProfile(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSave = async () => {
    setLocalSaving(true);
    try {
      await updateFranchiseProfile(localProfile);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save franchise profile:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setLocalSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalProfile(franchiseProfile);
    setIsEditing(false);
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const uploadedFile = await uploadDocument('franchise', file, type);
      setLocalProfile(prev => ({
        ...prev,
        [type]: uploadedFile.url
      }));
    }
  };

  return (
    <div className="container-fluid p-0">
      {/* Cover Image Section */}
      <div className="position-relative mb-4">
        <div
          className="bg-gradient rounded-4 position-relative overflow-hidden"
          style={{
            height: '200px',
            background: localProfile.coverImage
              ? `url(${localProfile.coverImage}) center/cover`
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}
        >
          <div className="position-absolute top-0 end-0 p-3">
            <label className="btn btn-dark btn-sm rounded-pill">
              <FiCamera className="me-2" />
              Change Cover
              <input
                type="file"
                className="d-none"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'coverImage')}
                disabled={!isEditing}
              />
            </label>
          </div>
        </div>

        {/* Profile Logo */}
        <div className="position-absolute" style={{ bottom: '-50px', left: '30px' }}>
          <div className="position-relative">
            <div
              className="bg-white rounded-circle border border-4 border-white shadow-lg d-flex align-items-center justify-content-center"
              style={{
                width: '120px',
                height: '120px',
                backgroundImage: localProfile.logo ? `url(${localProfile.logo})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              {!localProfile.logo && (
                <span className="fs-1 fw-bold text-primary">
                  {localProfile.franchiseName?.substring(0, 2).toUpperCase() || 'FR'}
                </span>
              )}
            </div>
            {isEditing && (
              <label className="position-absolute bottom-0 end-0 btn btn-primary btn-sm rounded-circle p-2">
                <FiCamera size={16} />
                <input
                  type="file"
                  className="d-none"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'logo')}
                />
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end mb-4 mt-5">
        {!isEditing ? (
          <button
            className="btn btn-primary rounded-pill px-4"
            onClick={() => setIsEditing(true)}
          >
            <FiEdit3 className="me-2" />
            Edit Profile
          </button>
        ) : (
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary rounded-pill px-4"
              onClick={handleCancel}
            >
              <FiX className="me-2" />
              Cancel
            </button>
            <button
              className="btn btn-primary rounded-pill px-4"
              onClick={handleSave}
            >
              <FiSave className="me-2" />
              Save Changes
            </button>
          </div>
        )}
      </div>

      {/* Profile Information */}
      <div className="row g-4">
        {/* Basic Information */}
        <div className="col-lg-6">
          <div className="glass-card p-4 h-100">
            <h5 className="fw-bold mb-4">Basic Information</h5>

            <div className="mb-3">
              <label className="form-label small text-muted">Franchise Name</label>
              <input
                type="text"
                className="form-control"
                value={localProfile.franchiseName}
                onChange={(e) => handleInputChange('franchiseName', e.target.value)}
                disabled={!isEditing}
                placeholder="Enter franchise name"
              />
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label small text-muted">Franchise ID</label>
                <input
                  type="text"
                  className="form-control"
                  value={localProfile.franchiseId}
                  onChange={(e) => handleInputChange('franchiseId', e.target.value)}
                  disabled={!isEditing}
                  placeholder="FR-XXXX"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small text-muted">Business Type</label>
                <select
                  className="form-select"
                  value={localProfile.businessType}
                  onChange={(e) => handleInputChange('businessType', e.target.value)}
                  disabled={!isEditing}
                >
                  <option value="">Select Type</option>
                  <option value="retail">Retail Store</option>
                  <option value="wholesale">Wholesale</option>
                  <option value="both">Retail & Wholesale</option>
                  <option value="online">Online Only</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
            </div>

            <div className="row g-3 mt-1">
              <div className="col-md-6">
                <label className="form-label small text-muted">Registration Number</label>
                <input
                  type="text"
                  className="form-control"
                  value={localProfile.registrationNumber}
                  onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
                  disabled={!isEditing}
                  placeholder="REG-XXXXXXXXX"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small text-muted">GST Number</label>
                <input
                  type="text"
                  className="form-control"
                  value={localProfile.gstNumber}
                  onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                  disabled={!isEditing}
                  placeholder="GSTIN"
                />
              </div>
            </div>

            <div className="mt-3">
              <label className="form-label small text-muted">Established Date</label>
              <input
                type="date"
                className="form-control"
                value={localProfile.establishedDate}
                onChange={(e) => handleInputChange('establishedDate', e.target.value)}
                disabled={!isEditing}
              />
            </div>
          </div>
        </div>

        {/* Contact Details */}
        <div className="col-lg-6">
          <div className="glass-card p-4 h-100">
            <h5 className="fw-bold mb-4">Contact Details</h5>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label small text-muted">Primary Phone</label>
                <input
                  type="tel"
                  className="form-control"
                  value={localProfile.contactDetails.primaryPhone}
                  onChange={(e) => handleInputChange('primaryPhone', e.target.value, true, 'contactDetails')}
                  disabled={!isEditing}
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small text-muted">Secondary Phone</label>
                <input
                  type="tel"
                  className="form-control"
                  value={localProfile.contactDetails.secondaryPhone}
                  onChange={(e) => handleInputChange('secondaryPhone', e.target.value, true, 'contactDetails')}
                  disabled={!isEditing}
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>
            </div>

            <div className="mt-3">
              <label className="form-label small text-muted">Email Address</label>
              <input
                type="email"
                className="form-control"
                value={localProfile.contactDetails.email}
                onChange={(e) => handleInputChange('email', e.target.value, true, 'contactDetails')}
                disabled={!isEditing}
                placeholder="franchise@example.com"
              />
            </div>

            <div className="mt-3">
              <label className="form-label small text-muted">
                <FiGlobe className="me-2" />
                Website
              </label>
              <input
                type="url"
                className="form-control"
                value={localProfile.contactDetails.website}
                onChange={(e) => handleInputChange('website', e.target.value, true, 'contactDetails')}
                disabled={!isEditing}
                placeholder="https://www.example.com"
              />
            </div>

            {/* Social Media */}
            <div className="mt-4">
              <label className="form-label small text-muted mb-3">Social Media</label>
              <div className="row g-2">
                <div className="col-6">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text"><FiFacebook /></span>
                    <input
                      type="text"
                      className="form-control"
                      value={localProfile.socialMedia.facebook}
                      onChange={(e) => handleInputChange('facebook', e.target.value, true, 'socialMedia')}
                      disabled={!isEditing}
                      placeholder="Facebook"
                    />
                  </div>
                </div>
                <div className="col-6">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text"><FiInstagram /></span>
                    <input
                      type="text"
                      className="form-control"
                      value={localProfile.socialMedia.instagram}
                      onChange={(e) => handleInputChange('instagram', e.target.value, true, 'socialMedia')}
                      disabled={!isEditing}
                      placeholder="Instagram"
                    />
                  </div>
                </div>
                <div className="col-6">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text"><FiTwitter /></span>
                    <input
                      type="text"
                      className="form-control"
                      value={localProfile.socialMedia.twitter}
                      onChange={(e) => handleInputChange('twitter', e.target.value, true, 'socialMedia')}
                      disabled={!isEditing}
                      placeholder="Twitter"
                    />
                  </div>
                </div>
                <div className="col-6">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text"><FiLinkedin /></span>
                    <input
                      type="text"
                      className="form-control"
                      value={localProfile.socialMedia.linkedin}
                      onChange={(e) => handleInputChange('linkedin', e.target.value, true, 'socialMedia')}
                      disabled={!isEditing}
                      placeholder="LinkedIn"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div className="col-12">
          <div className="glass-card p-4">
            <h5 className="fw-bold mb-4">Business Address</h5>

            <div className="row g-3">
              <div className="col-12">
                <label className="form-label small text-muted">Street Address</label>
                <input
                  type="text"
                  className="form-control"
                  value={localProfile.address.street}
                  onChange={(e) => handleInputChange('street', e.target.value, true, 'address')}
                  disabled={!isEditing}
                  placeholder="Enter street address"
                />
              </div>

              <div className="col-md-3">
                <label className="form-label small text-muted">City</label>
                <input
                  type="text"
                  className="form-control"
                  value={localProfile.address.city}
                  onChange={(e) => handleInputChange('city', e.target.value, true, 'address')}
                  disabled={!isEditing}
                  placeholder="City"
                />
              </div>

              <div className="col-md-3">
                <label className="form-label small text-muted">State</label>
                <input
                  type="text"
                  className="form-control"
                  value={localProfile.address.state}
                  onChange={(e) => handleInputChange('state', e.target.value, true, 'address')}
                  disabled={!isEditing}
                  placeholder="State"
                />
              </div>

              <div className="col-md-3">
                <label className="form-label small text-muted">PIN Code</label>
                <input
                  type="text"
                  className="form-control"
                  value={localProfile.address.pincode}
                  onChange={(e) => handleInputChange('pincode', e.target.value, true, 'address')}
                  disabled={!isEditing}
                  placeholder="PIN Code"
                />
              </div>

              <div className="col-md-3">
                <label className="form-label small text-muted">Country</label>
                <input
                  type="text"
                  className="form-control"
                  value={localProfile.address.country}
                  onChange={(e) => handleInputChange('country', e.target.value, true, 'address')}
                  disabled={!isEditing}
                  placeholder="Country"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FranchiseProfile;